import React from "react";
import { ComponentProps, onActivate, RichText } from "api-sdk-react";
import {
    BooleanInputField,
    BooleanNodeInput,
    DateInputField,
    DateInputUtils,
    DateNodeInput,
    DynamicChoiceNodeInput, FileMap, FileNodeInput,
    getCommaCharacter, isChatNode, isDisplayNode, isInputNode,
    MultipleChoiceInputField,
    MultipleChoiceNodeInput,
    Node,
    NodeInput,
    NodeInputMap,
    NumberNodeInput,
    RunState,
    SingleChoiceInputField,
    SingleChoiceNodeInput,
    TextNodeInput
} from "api-sdk"
import { ReaderConfig, ReaderIcon } from "reader-common";
import { ChatStyleConfig } from "../../types";
import { ChatMessageHeader } from "./chatMessageHeader";

export interface UserMessageProps extends ComponentProps {
    node: Node,
    nodeInputMap: NodeInputMap,
    fileMap: FileMap,
    chatMessageDraft?: string,
    runState: RunState,
    config: ReaderConfig,
    styleConfig: ChatStyleConfig,
    onEdit: () => void,
    canEdit?: boolean,
    animateLeave?: boolean // Whether to show a leaving animation
}

export function UserMessage(props: UserMessageProps) {

    function renderUserBlockContent() {
        if (isChatNode(props.node) && props.chatMessageDraft) {
            return props.chatMessageDraft
        }

        if (isDisplayNode(props.node) && !props.node.isEndNode) {
            return props.config.messages.passiveAnswer
        }

        let texts: string[] = []

        if (isInputNode(props.node)) {
            props.node.inputFields.forEach(field => {
                const nodeInput: Partial<NodeInput> = props.nodeInputMap[field.id] || { type: field.valueType }

                switch (field.valueType) {
                    case "SINGLE_CHOICE": {
                        const opt = (field as SingleChoiceInputField).options
                            .find(opt => opt.id == (nodeInput as SingleChoiceNodeInput).optionId)
                        texts.push(opt?.label);
                        break;
                    }
                    case "NUMBER": {
                        const number = (nodeInput as NumberNodeInput).number
                        texts.push((number != null ? String(number) : "")
                            .replace(/[.,]/g, getCommaCharacter(props.runState.locale)))
                        break;
                    }
                    case "TEXT": {
                        texts.push((nodeInput as TextNodeInput).text || "");
                        break;
                    }
                    case "DATE": {
                        const date = (nodeInput as DateNodeInput).date
                        if (date != null && date.length) {
                            texts.push(DateInputUtils.formatISODateString(field as DateInputField, date));
                        }
                        break;
                    }
                    case "BOOLEAN": {
                        const value = (nodeInput as BooleanNodeInput).value
                        const inputLabel = value != null
                            ? (value ? ((field as BooleanInputField).trueLabel || "✓") : ((field as BooleanInputField).falseLabel || "x"))
                            : null
                        texts.push(inputLabel)
                        break;
                    }
                    case "FILE": {
                        const isFilePresent = (nodeInput as FileNodeInput).file?.id != undefined || props.fileMap[field.id] != undefined;
                        texts.push(isFilePresent ? props.config.messages.inputFile : null);
                        break;
                    }
                    case "MULTIPLE_CHOICE": {
                        // TODO for some reason indices sometimes appear twice in the selected options list
                        const selectedDistinct = ((nodeInput as MultipleChoiceNodeInput).selectedOptionIds || []).filter((v, idx, ar) => ar.indexOf(v) == idx)

                        if (!selectedDistinct.length && nodeInput.type === "MULTIPLE_CHOICE") {
                            texts.push(field.noneOption?.label || props.config.messages.skip)
                        } else {
                            const selectedLabels = selectedDistinct
                                .map(selId => (field as MultipleChoiceInputField).options.find(opt => opt.id == selId))
                                .sort((a, b) => a?.idx - b?.idx)
                                .map(opt => {
                                    const label = opt?.label
                                    return label ? label.replace(/<p>/g, "<span>").replace(/<\/p>/g, "</span>") : ""
                                })
                            texts.push(selectedLabels.length ? selectedLabels.join(", ") : null)
                        }

                        break;
                    }
                    case "DYNAMIC_CHOICE": {
                        texts.push((nodeInput as DynamicChoiceNodeInput).label);
                        break;
                    }
                }
            })
        }

        texts = texts.filter(text => !!text && text.length) // Filter out empty text

        return (texts.length && texts.some(t => !!t.length))
            ? texts.join(", ")
            : props.config.messages.skip
    }

    const canEdit = isInputNode(props.node) && props.onEdit && props.canEdit

    return (
        <div
            className={"message user-message " + (props.className || "") + (props.animateLeave ? " animated-leave" : "")}>
            <ChatMessageHeader config={props.config} styleConfig={props.styleConfig} target="user"/>
            <div className="message-content" key="message-content" role="document">
                <RichText text={ renderUserBlockContent() }/>
                <div
                    className={"edit-answer " + (canEdit ? "" : "hidden ")}
                    role="button"
                    {...onActivate(canEdit ? () => props.onEdit() : undefined)}>
                    <ReaderIcon iconName="edit"/>
                    <div className="text">
                        {props.config.messages.editAnswer.toUpperCase()}
                    </div>
                </div>
            </div>
        </div>
    )

}
